/*
 * admin-extras/tx-tags.js
 * =======================
 * THE SINGLE SOURCE OF TRUTH for strichliste transaction-comment tags on the
 * JavaScript side. One physical file, two consumers:
 *
 *   1. The admin SPA — web/src/utils/txComment.ts imports it directly (relative
 *      path; web/vite.config.ts `fs.allow` lets Vite reach outside web/). This
 *      file is REAL ESM so Vite's dev server (which serves source `.js` as
 *      native ESM) resolves the `export`s without any CommonJS interop.
 *   2. The injected kiosk overrides — ui-overrides.js is a CLASSIC script and
 *      reads `window.TxTags`. So this file is injected on the kiosk as
 *      `<script type="module" src="…/tx-tags.js">` (see docker/config/default.conf)
 *      and sets the global below. Module scripts run after parsing but BEFORE
 *      DOMContentLoaded, and ui-overrides defers its first pass to
 *      DOMContentLoaded, so `window.TxTags` is always ready before it decorates.
 *
 * Why it lives HERE (admin-extras, not web/src): the kiosk can only serve files
 * out of the bind-mounted admin-extras/ dir and runs no build step, so the
 * canonical file has to sit where the kiosk serves from. The admin build, which
 * can resolve any path, reaches across to it — one file, no generated copy to
 * drift. (app/strichliste/comment_tags.py remains the hand-kept Python mirror.)
 *
 * Grammar — mirrors comment_tags.py::_TAG_RE EXACTLY (the tag is persisted in
 * strichliste and matched by reconciliation, so its shape is an on-disk
 * contract — never change it without rewriting every stored comment):
 *
 *     [<verb>:<id>[/<modifier>]] <freetext>
 *
 * NOTE: parse() expects the CANONICAL stored comment (starts with `[`). The
 * kiosk's rendered DOM sometimes prefixes a `:` (strichliste joins
 * "name:comment"); the kiosk strips that before calling parse().
 */

export const TAG_RE = /^\[([a-z_]+):([^/\]]+)(?:\/([^\]]+))?\]\s*(.*)$/;

/**
 * Split a comment into { verb, id, modifier, text }, or null if it carries no
 * `[verb:id…]` tag. Generic only — per-verb id validation lives elsewhere.
 */
export function parse(comment) {
  if (!comment) return null;
  const m = String(comment).match(TAG_RE);
  if (!m) return null;
  return { verb: m[1], id: m[2], modifier: m[3] || null, text: m[4] || "" };
}

/**
 * Render metadata per KNOWN verb: a human label + an AntD colour name. The
 * admin renders these as <Tag color=…>; the kiosk maps the colour name (or the
 * verb) to a CSS swatch. Unknown verbs (incl. the legacy `reenable`) render as
 * raw freetext. `voucher`/`voucher_sweep` are here so both surfaces badge them.
 */
export const VERB_META = {
  settle: { label: "Settlement", color: "blue" },
  correct: { label: "Correction", color: "orange" },
  voucher: { label: "Voucher", color: "green" },
  voucher_sweep: { label: "Sweep", color: "default" },
  volunteer: { label: "Volunteer comp", color: "purple" },
};

const TxTags = { TAG_RE, parse, VERB_META };
export default TxTags;

// Expose to the classic-script kiosk overrides (ui-overrides.js reads
// window.TxTags). No-op in non-browser contexts (e.g. SSR/tests guard on it).
if (typeof window !== "undefined") {
  window.TxTags = TxTags;
}
